import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Award, Filter } from "lucide-react";
import QstLayout from "./QstLayout";
import QstAssistantWidget from "./QstAssistantWidget";
import { qstTrades } from "./api";

export default function QstTradesPage() {
  const [data, setData] = useState({ live: [], coming_soon: [] });
  useEffect(() => {
    document.title = "Trades — Quality Safety Times";
    qstTrades().then(setData).catch(() => {});
  }, []);

  return (
    <QstLayout>
      <QstAssistantWidget context={{ page: "trade" }} />
      <section className="bg-gradient-to-b from-[#0B2545] to-[#1D4ED8] text-white py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-[#F97316] font-bold">Trades</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight">Every Trade, Every Lesson</h1>
          <p className="mt-3 text-lg text-slate-200 max-w-2xl">
            Quality Safety Times covers {data.live.length} live construction trades — with more launching every quarter.
            Every lesson is authored and reviewed by an experienced construction professional.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12" data-testid="qst-trades-live">
        <h2 className="text-2xl font-bold text-[#0B2545]">Available Now</h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.live.map((t) => (
            <Link key={t.slug} to={`/qst/trades/${t.slug}`}
              className="group rounded-xl overflow-hidden bg-white border border-slate-200 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              data-testid={`qst-trade-tile-${t.slug}`}>
              <div className="h-36 bg-slate-200 overflow-hidden relative">
                {t.hero && <img src={t.hero} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent" />
                <div className="absolute bottom-3 left-3 text-white">
                  <div className="text-lg font-bold">{t.name}</div>
                  {t.subtitle && <div className="text-[11px] opacity-90">{t.subtitle}</div>}
                </div>
              </div>
              <div className="p-5">
                <p className="text-sm text-slate-700 leading-relaxed line-clamp-2">{t.summary}</p>
                <div className="mt-4 flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest">
                  <span className="text-slate-600">{t.lesson_count} lessons</span>
                  {t.quality_count > 0 && <span className="text-[#F97316]">{t.quality_count} QC</span>}
                  {t.safety_count > 0 && <span className="text-[#DC2626]">{t.safety_count} SAFETY</span>}
                </div>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#F97316] group-hover:translate-x-0.5 transition">
                  Explore Trade <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-12" data-testid="qst-trades-coming">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-[#0B2545]">Coming Soon</h2>
          <p className="mt-1 text-sm text-slate-600">Join the waitlist and we'll notify you as new trades ship.</p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {data.coming_soon.map((t) => (
              <div key={t.slug} className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-center">
                <div className="text-sm font-bold text-[#0B2545]">{t.name}</div>
                {t.subtitle && <div className="text-[11px] text-slate-500">{t.subtitle}</div>}
                <Link to={`/qst/contact?intent=waitlist&trade=${t.slug}`}
                  className="mt-3 inline-block text-[11px] font-bold text-[#F97316] hover:underline">
                  Join waitlist
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </QstLayout>
  );
}
